/* Perfil e contexto de drill-down do painel.

   Diferença central em relação ao projeto de origem (Analytics_engajamento):
   lá o perfil era ESCOLHIDO pelo usuário e guardado em localStorage. Aqui ele
   é DERIVADO do papel ativo no Redux do AVA (general.active_roles.role_id) —
   ver decisão D3 do plano. O localStorage guarda apenas o contexto de
   drill-down (qual turma/escola/região está aberta), que é preferência de
   navegação, não permissão.

   Desde 04/08/2026 a derivação é ESTRITA: um papel ativo, um perfil. Não há
   mais escolha do usuário nem união com os outros papéis dele — o que sobrava
   de "escolher" no projeto de origem saiu junto com a tela de seleção. Ver a
   nota em config/perfilPorRole.js.

   Trocar de papel no AVA limpa o contexto: os ids de turma/escola do papel
   anterior não valem para o novo. */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { escopoDoRole, perfilDoRole } from "../config/perfilPorRole";

const Ctx = createContext(null);

const CHAVE_CONTEXTO = "educandus.ie.contexto";

const lerContexto = () => {
  try {
    const bruto = localStorage.getItem(CHAVE_CONTEXTO);
    return bruto ? JSON.parse(bruto) : null;
  } catch (err) {
    return null;
  }
};

export function PerfilProvider({ children }) {
  // Só o papel ATIVO entra aqui. `user.user_roles` (a lista de todos os papéis
  // do usuário) era lida junto e é justamente o que não deve pesar: era ela que
  // fazia um usuário com dois papéis ver as duas visões.
  const activeRoles = useSelector(state => state.general.active_roles);

  const roleId = activeRoles?.role_id;
  const grupoId = activeRoles?.group_id;

  // O perfil é o do papel ativo, e não há segunda opção: nada de escolha do
  // usuário, nada de somar os outros papéis dele. Ver a nota em perfilPorRole.js.
  const perfil = perfilDoRole(roleId);

  const [contexto, setContextoRaw] = useState(lerContexto);

  // Troca de papel (ou de grupo) invalida o contexto de drill-down: os ids de
  // turma/escola do papel anterior não valem para o novo.
  useEffect(() => {
    setContextoRaw(null);
    localStorage.removeItem(CHAVE_CONTEXTO);
  }, [roleId, grupoId]);

  const setContexto = useCallback(novo => {
    setContextoRaw(novo);
    if (novo) {
      localStorage.setItem(CHAVE_CONTEXTO, JSON.stringify(novo));
    } else {
      localStorage.removeItem(CHAVE_CONTEXTO);
    }
  }, []);

  const valor = useMemo(
    () => ({
      perfil,
      escopo: escopoDoRole(roleId),
      roleId,
      grupoId,
      contexto,
      setContexto
    }),
    [perfil, roleId, grupoId, contexto, setContexto]
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

PerfilProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export function usePerfil() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("usePerfil fora do PerfilProvider");
  return ctx;
}
